import { useState } from "react";
import useApi from "./useApi";
import { API_ROUTES } from "lib/constants/api-constants";
import { PiHospitalLight } from "react-icons/pi";
import { DMS_PATHS } from "lib/constants/dms-path-constants";
import { DMSServices } from "lib/services/document-services";
import useDocLinks from "./useDocLinks";

interface ClinicState {
  error?: string | null;
  loading: boolean;
  clinics: Clinic[];
  calenderDetails: CalendarDetails;
  currentDate: string | null;
  getClinics: (
    isGetAll: boolean,
    onSuccess?: (clinics: Clinic[]) => void
  ) => void;
  getClinicById: (
    clinicId: string,
    onSuccess?: (clinics: Clinic) => void
  ) => void;

  getCalenderDetails: (
    clinicId: string,
    onSuccess?: (calenderDetails: CalendarDetails) => void
  ) => void;
  updateCalendarDetails: (
    clinicId: string,
    calenderDetails: CalendarDetails,
    onSuccess: (calenderDetails: CalendarDetails) => void
  ) => void;
  onSelectClinicDate: (date: string, status: string) => void;
  updateClinic: (clinic: Clinic, onSuccess?: (clinic: Clinic) => void) => void;
  addClinic: (clinic: Clinic, onSuccess?: (clinic: Clinic) => void) => void;
  setCurrentDate: (date: string) => void;
  getClinicWidget: (
    clinic: Clinic,
    index: number,
    size?: number
  ) => ClinicWidget;
  getClinicWidgets: (clinics: Clinic[], size?: number) => ClinicWidget[];
  updateClinicProfilePic: (
    clinicId: string,
    profilePic: File,
    onSuccess?: (profilePicUrl: string) => void
  ) => void;

  getClinicProfilePic: (
    clinicId: string,
    onSuccess?: (profilePicUrl: string) => void
  ) => void;

  getClinicIncome: (
    clinicId: string,
    onSuccess?: (profilePicUrl: ClinicIncome) => void
  ) => void;
}

const useClinics = (): ClinicState => {
  const {
    createDocLink,
    error: docLinksError,
    isLoading: docLinksLoading,
    getDocLink,
  } = useDocLinks();
  const { getData: getClinicsRequest, loading: getClinicsRequestLoading } =
    useApi<Clinic[]>();
  const {
    getData: getClinicByIdRequest,
    loading: getClinicByIdRequestLoading,
  } = useApi<Clinic>();

  const { postData: addClinicRequest, loading: addClinicRequestLoading } =
    useApi<Clinic>();
  const { loading: updateClinicRequestLoading, putData: updateClinicRequest } =
    useApi<Clinic>();
  const {
    data: calenderDetails,
    getData: getCalenderDetailsRequest,
    loading: getCalenderDetailsRequestLoading,
  } = useApi<CalendarDetails>();
  const {
    putData: updateCalenderDetailsRequest,
    loading: updateCalenderDetailsRequestLoading,
  } = useApi<CalendarDetails>();

  const {
    getData: getClinicIncomeRequest,
    loading: getClinicIncomeRequestLoading,
  } = useApi<ClinicIncome>();

  const [error, setError] = useState<string | null>();
  const [clinics, setClinics] = useState<Clinic[]>([]);
  const [selectedClinicDate, setSelectedClinicDate] = useState<string | null>(
    null
  );
  const onFailure = (errorMessage: string) => {
    setError(errorMessage);
  };

  const getClinics = async (
    isGetAll: boolean = false,
    onSuccess?: (clinics: Clinic[]) => void
  ) => {
    setError(null);
    await getClinicsRequest(
      isGetAll ? API_ROUTES.CLINICS.all : API_ROUTES.CLINICS.base,
      (value) => {
        const response = ((value as Clinic[]) ?? [])
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((item, index) => {
            item.index = index + 1;
            return item;
          });
        setClinics(response);
        onSuccess?.(response);
      },
      onFailure
    );
  };

  const getClinicById = async (
    clinicId: string,
    onSuccess?: (clinic: Clinic) => void
  ) => {
    setError(null);
    await getClinicByIdRequest(
      `${API_ROUTES.CLINICS.base}/${clinicId}`,
      onSuccess,
      onFailure
    );
  };

  const getCalenderDetails = async (
    clinicId: string,
    onSuccess?: (clinics: CalendarDetails) => void
  ) => {
    setError(null);
    await getCalenderDetailsRequest(
      `${API_ROUTES.CLINIC_CALENDAR.base}/${clinicId}`,
      (value) => {
        onSuccess?.(value as CalendarDetails);
      },
      onFailure
    );
  };

  const onSelectClinicDate = (date: string, status: string) => {
    setSelectedClinicDate(date);
    //setCalendarStatus(status);
    console.log("Selected Date: ", selectedClinicDate);
    console.log("Status: ", status);
  };

  const updateCalendarDetails = async (
    clinicId: string,
    calenderDetails: CalendarDetails,
    onSuccess?: (calenderDetails: CalendarDetails) => void
  ) => {
    setError(null);
    await updateCalenderDetailsRequest(
      `${API_ROUTES.CLINIC_CALENDAR.base}/${clinicId}`,
      calenderDetails,
      onSuccess,
      onFailure
    );
  };

  const updateClinic = async (
    clinic: Clinic,
    onSuccess?: (clinic: Clinic) => void
  ) => {
    setError(null);

    await updateClinicRequest(
      `${API_ROUTES.CLINICS.base}/${clinic?.id}`,
      clinic,
      onSuccess,
      onFailure
    );
  };

  const setCurrentDate = (date: string) => {
    setSelectedClinicDate(date);
  };

  const addClinic = async (
    request: Clinic & CalendarDetails,
    onSuccess?: (clinic: Clinic) => void
  ) => {
    setError(null);
    await addClinicRequest(
      API_ROUTES.CLINICS.base,
      request,
      onSuccess,
      onFailure
    );
  };

  const getClinicWidget = (
    clinic: Clinic,
    index: number,
    size?: number
  ): ClinicWidget => {
    return {
      index: index,
      id: clinic.id,
      title: clinic.name,
      count: 0,
      total: 0,
      percentage: 0,
      icon: <PiHospitalLight size={size ?? 32} />,
    };
  };

  const getClinicWidgets = (
    clinics: Clinic[],
    size?: number
  ): ClinicWidget[] => {
    if (clinics?.length === 0) return [];
    return (clinics ?? []).map((clinic, index) =>
      getClinicWidget(clinic, index, size)
    );
  };

  const getClinicProfilePic = async (
    clinicId: string,
    onSuccess?: (profilePicUrl: string) => void
  ) => {
    // Query docLink to get the profile pic URL
    const docLinkDetails: Partial<DocLinkDetails> = {
      parentId: clinicId,
      path: `${DMS_PATHS.IMAGES.BASE}${DMS_PATHS.IMAGES.CLINICS}/${clinicId}${DMS_PATHS.IMAGES.CLINIC_PROFILE}`,
    };

    getDocLink(docLinkDetails.parentId, docLinkDetails.path, (response) =>
      onSuccess(response?.[0]?.url)
    );
  };

  const updateClinicProfilePic = async (
    clinicId: string,
    profilePic: File,
    onSuccess?: (profilePicUrl: string) => void
  ) => {
    const docLinkDetails: DocLinkDetails = {
      parentId: clinicId,
      parentType: "clinic",
      path: `${DMS_PATHS.IMAGES.BASE}${DMS_PATHS.IMAGES.CLINICS}/${clinicId}${DMS_PATHS.IMAGES.CLINIC_PROFILE}`,
      title: profilePic?.name,
      url: "", // <-- This will be updated after upload
    };

    // Compress file
    const compressedFile = await DMSServices.compressImage(profilePic);

    // Upload file to DMS
    DMSServices.uploadDocument(
      compressedFile,
      docLinkDetails.parentId,
      docLinkDetails.parentType,
      docLinkDetails.path,
      "",
      (response) => {
        // Create DocLink
        docLinkDetails.url = response.url;
        createDocLink(docLinkDetails, () => onSuccess(docLinkDetails.url));
      }
    );
  };

  const getClinicIncome = async (
    clinicId: string,
    onSuccess?: (profilePicUrl: ClinicIncome) => void
  ) => {
    const endPoint = API_ROUTES.CLINICS.income(clinicId);
    await getClinicIncomeRequest(endPoint, onSuccess, setError);
  };

  return {
    calenderDetails,
    clinics,
    currentDate: selectedClinicDate,
    error: error || docLinksError || DMSServices.isError(),
    loading:
      getClinicsRequestLoading ||
      updateClinicRequestLoading ||
      getCalenderDetailsRequestLoading ||
      getClinicByIdRequestLoading ||
      updateCalenderDetailsRequestLoading ||
      addClinicRequestLoading ||
      docLinksLoading ||
      DMSServices.isLoading() || getClinicIncomeRequestLoading,

    addClinic,
    getCalenderDetails,
    getClinicById,
    getClinicWidget,
    getClinicWidgets,
    getClinics,
    onSelectClinicDate,
    setCurrentDate,
    updateCalendarDetails,
    updateClinic,
    updateClinicProfilePic,
    getClinicProfilePic,
    getClinicIncome
  };
};
export default useClinics;
